import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Skeleton } from "@/components/ui/skeleton";
import { StatusBadge } from "@/components/StatusBadge";
import { OrderDetail } from "@/components/OrderDetail";
import { useAuth } from "@/context/AuthContext";
import { api, brl, fmtDate } from "@/lib/api";

function ResellerSummary({ orders }) {
  const paid = orders.filter((o) => o.payment_status === "paid");
  const open = orders.filter((o) => o.status !== "Disponível para retirada" && o.payment_status === "paid").length;
  const cards = [["Pedidos", orders.length, "reseller-kpi-orders"], ["Em andamento", open, "reseller-kpi-open"], ["Total pago", brl(paid.reduce((s, o) => s + o.total, 0)), "reseller-kpi-paid"]];
  return (
    <div className="mb-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
      {cards.map(([label, value, id]) => (
        <div key={id} className="rounded-md border border-slate-200 bg-white p-4" data-testid={id}>
          <p className="text-xs font-semibold uppercase tracking-wider text-slate-500">{label}</p>
          <p className="mt-1 font-mono text-2xl font-bold">{value}</p>
        </div>
      ))}
    </div>
  );
}

export default function AccountPage() {
  const { user } = useAuth();
  const [orders, setOrders] = useState(null);
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    api.get("/orders/mine").then((r) => {
      setOrders(r.data);
      setSelected(r.data[0] || null);
    }).catch(() => setOrders([]));
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-12 sm:px-6 lg:px-8">
      {user?.role === "reseller" && (
        <p className="mb-2 inline-flex rounded-full bg-violet-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-violet-800" data-testid="reseller-area-badge">Área do revendedor</p>
      )}
      {user?.role === "store" && (
        <p className="mb-2 inline-flex rounded-full bg-emerald-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-emerald-800" data-testid="store-area-badge">Área da loja</p>
      )}
      {user?.role === "vip" && (
        <p className="mb-2 inline-flex rounded-full bg-amber-100 px-3 py-1 text-xs font-bold uppercase tracking-wider text-amber-800" data-testid="vip-area-badge">Cliente VIP</p>
      )}
      <h1 className="font-display text-3xl font-extrabold tracking-tight sm:text-4xl" data-testid="account-title">Olá, {user?.name?.split(" ")[0]}</h1>
      <p className="mb-8 text-slate-500">{{ reseller: "Seus pedidos com preço de revenda, o andamento de cada um e as notas de serviço.", store: "Pedidos da loja para os clientes do balcão, o andamento de cada um e as notas de serviço.", vip: "Você tem 10% de desconto nos produtos selecionados. Aqui ficam seus pedidos, o andamento de cada um e as notas de serviço." }[user?.role] || "Seus pedidos e o andamento de cada um."}</p>
      {["reseller", "store"].includes(user?.role) && orders?.length > 0 && <ResellerSummary orders={orders} />}
      {orders === null ? (
        <Skeleton className="h-64 rounded-md" />
      ) : orders.length === 0 ? (
        <div className="rounded-md border border-dashed border-slate-300 bg-white p-12 text-center" data-testid="account-no-orders">
          <p className="text-slate-500">Você ainda não fez pedidos com esta conta.</p>
          <Link to="/" className="mt-3 inline-block font-semibold text-sky-600">Ver produtos</Link>
        </div>
      ) : (
        <div className="grid gap-6 lg:grid-cols-12">
          <ul className="space-y-2 lg:col-span-4" data-testid="account-orders-list">
            {orders.map((o) => (
              <li key={o.id}>
                <button onClick={() => setSelected(o)} data-testid={`account-order-${o.number}`}
                  className={`w-full rounded-md border p-4 text-left transition-colors ${selected?.id === o.id ? "border-[#0099DD] bg-sky-50" : "border-slate-200 bg-white hover:border-slate-300"}`}>
                  <div className="flex items-center justify-between">
                    <span className="font-mono font-bold">#{o.number}</span>
                    <StatusBadge status={o.status} testId={`account-order-status-${o.number}`} />
                  </div>
                  <p className="mt-1 text-xs text-slate-500">{fmtDate(o.created_at)} · {brl(o.total)}</p>
                </button>
              </li>
            ))}
          </ul>
          <div className="lg:col-span-8">{selected && <OrderDetail order={selected} />}</div>
        </div>
      )}
    </div>
  );
}
